import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IWindowFunctionItem,
  WindowFunctionModalViews,
} from '../constants/interfaces/windowFunctions.interface';
import { propertiesListModalsSlice } from 'Modules/Studio/Questions/EditQuestion/PropertiesList/redux/propertiesListModals.slice';

const { LIST, EDIT } = WindowFunctionModalViews;
export interface IWindowFunctionsModalState {
  currentView: WindowFunctionModalViews;
  displayConfirmCancel: boolean;
  displayConfirmDelete: boolean;
  selectedIndex: number | null;
  selectedPropWindowFunctions: IWindowFunctionItem[];
  isNewWF: boolean;
}

export const initialState: IWindowFunctionsModalState = {
  currentView: WindowFunctionModalViews.LIST,
  displayConfirmCancel: false,
  displayConfirmDelete: false,
  selectedIndex: null,
  selectedPropWindowFunctions: [],
  isNewWF: false,
};

export const WindowFunctionsModalReducer = {
  resetState: (state: IWindowFunctionsModalState) => {
    state.currentView = initialState.currentView;
    state.displayConfirmCancel = initialState.displayConfirmCancel;
    state.displayConfirmDelete = initialState.displayConfirmDelete;
    state.selectedIndex = initialState.selectedIndex;
    state.selectedPropWindowFunctions = initialState.selectedPropWindowFunctions;
    state.isNewWF = initialState.isNewWF;
  },

  setCurrentView: (
    state: IWindowFunctionsModalState,
    action: PayloadAction<{
      currentView: IWindowFunctionsModalState['currentView'];
      selectedIndex: IWindowFunctionsModalState['selectedIndex'];
      isNewWF?: IWindowFunctionsModalState['isNewWF'];
    }>
  ) => {
    state.currentView = action.payload.currentView;
    state.selectedIndex = action.payload.selectedIndex;
    state.isNewWF = action.payload.isNewWF || initialState.isNewWF;

    // If the selectedIndex is null, also set the confirm states to false
    // This is done when the user either cancel's or confirm's an action on the modal
    if (action.payload.selectedIndex === null) {
      state.displayConfirmCancel = false;
      state.displayConfirmDelete = false;
    }
  },

  openConfirmCancel: (state: IWindowFunctionsModalState) => {
    state.displayConfirmCancel = true;
  },

  closeConfirmCancel: (state: IWindowFunctionsModalState) => {
    state.displayConfirmCancel = false;
  },

  openConfirmDelete: (state: IWindowFunctionsModalState) => {
    state.displayConfirmDelete = true;
  },

  closeConfirmDelete: (state: IWindowFunctionsModalState) => {
    state.displayConfirmDelete = false;
  },
};

export const WindowFunctionsModalSlice = createSlice({
  name: 'windowFunctionsModal',
  initialState,
  reducers: WindowFunctionsModalReducer,
  extraReducers(builder) {
    builder.addCase(propertiesListModalsSlice.actions.toggleModal, (state, action) => {
      if (action.payload.type === 'windowFunctions') {
        if (!action.payload.selectedProp && !action.payload.selectedCalc) {
          // reset state
          WindowFunctionsModalSlice.caseReducers.resetState(state);
          return;
        }

        let windowFunctions: IWindowFunctionItem[] = [];

        if (action.payload.selectedProp !== null) {
          windowFunctions = action.payload.selectedProp?.windowFunctions || [];
        } else if (action.payload.selectedCalc) {
          windowFunctions = action.payload.selectedCalc?.windowFunctions || [];
        }

        const selectedWindowFunction =
          action.payload.additionalProps?.windowFunctionsModal?.selectedWindowFunction;

        const selectedIndex = windowFunctions.findIndex((wf) => wf === selectedWindowFunction);
        const isFound = selectedIndex !== -1;

        // assign state
        state.currentView = isFound ? EDIT : LIST;
        state.selectedPropWindowFunctions = windowFunctions;
        state.selectedIndex = selectedIndex !== -1 ? selectedIndex : state.selectedIndex;
        state.isNewWF = false;
      }
    });
  },
});

export const {
  resetState,
  setCurrentView,
  openConfirmCancel,
  closeConfirmCancel,
  openConfirmDelete,
  closeConfirmDelete,
} = WindowFunctionsModalSlice.actions;

export default WindowFunctionsModalSlice.reducer;
